import {
  Children,
  cloneElement,
  forwardRef,
  isValidElement,
  ReactElement,
  useEffect,
  useState,
} from "react";

import { DateTime } from "luxon";
import { rem } from "polished";

import { DatePicker, DateSelector } from "emoreg/atoms/date-picker";
import { Divider } from "emoreg/atoms/Divider";
import { InputSwitch } from "emoreg/atoms/Inputs/InputSwitch";
import { TimePicker, TimePickerRefApi } from "emoreg/atoms/time-picker";
import { Typography } from "emoreg/atoms/Typography";
import { Flex } from "emoreg/layouts";

const DEFAULT_TIME_VALUE = "00:00";

type DatetimePickerProps = {
  /** string value (format: yyyy-mm-ddThh:mm) */
  value?: string | null;
  /** Combined datetime change handler */
  onValueChange?: (value: string) => void;
  /** Initial enabled state for time selection (defaults to true) */
  initialTimeEnabled?: boolean;
  children: React.ReactNode;
};

export const DatetimePicker = ({
  value,
  onValueChange,
  initialTimeEnabled,
  children,
}: DatetimePickerProps) => {
  const [date, setDateState] = useState<string | null>(value?.split("T")[0] || null);
  const [time, setTimeState] = useState<string | null>(value?.split("T")[1] || DEFAULT_TIME_VALUE);
  const [isTimeEnabled, setTimeEnabled] = useState<boolean>(
    initialTimeEnabled !== undefined ? initialTimeEnabled : true
  );
  const [targetMonth, setTargetMonth] = useState<string | null>(
    value?.split("T")[0]?.substring(0, 7) || DateTime.now().toFormat("yyyy-MM")
  );

  // keep local data updated with external data on change
  useEffect(() => {
    const [newDate, newTime] = value?.split("T") || [null, DEFAULT_TIME_VALUE];
    setDateState(newDate || null);
    setTimeState(newTime || DEFAULT_TIME_VALUE);
    if (newDate) {
      setTargetMonth(newDate.substring(0, 7));
    } else {
      // If no date value, default to current month
      setTargetMonth(DateTime.now().toFormat("yyyy-MM"));
    }
  }, [value]);

  const setDate = (newDate: string) => {
    setDateState(newDate);
    setTargetMonth(newDate.substring(0, 7));

    const effectiveTime = isTimeEnabled ? time : DEFAULT_TIME_VALUE;
    if (newDate && effectiveTime) {
      onValueChange?.(`${newDate}T${effectiveTime}`);
    }
  };

  const setTime = (newTime: string) => {
    setTimeState(newTime);

    // Only trigger combined onChange if time is enabled and date exists
    if (isTimeEnabled && date && newTime) {
      onValueChange?.(`${date}T${newTime}`);
    }
  };

  const handleTimeEnabledChange = (enabled: boolean) => {
    setTimeState("00:00");
    setTimeEnabled(enabled);

    // When toggling, update the combined value
    if (date) {
      const effectiveTime = enabled ? DEFAULT_TIME_VALUE : DEFAULT_TIME_VALUE;
      onValueChange?.(`${date}T${effectiveTime}`);
    }
  };

  const childList = Children.toArray(children);
  const dateComp = childList.find(isDatetimePickerDateComp);
  const timeComp = childList.find(isDatetimePickerTimeComp);

  if (!dateComp || !timeComp) {
    throw new Error("DatetimePicker must have a DatetimePicker.Date and DatetimePicker.Time child");
  }

  const dateProps = {
    date,
    setDate,
    targetMonth,
    setTargetMonth,
  };

  const timeProps = {
    time,
    setTime,
    isTimeEnabled,
    setTimeEnabled: handleTimeEnabledChange,
  };

  return (
    <Flex gap={16} h={rem(320)} alignItems="stretch">
      {dateComp && (
        <>
          {cloneElement(dateComp, { ...dateProps, ...dateComp.props })}
          {timeComp && <Divider orientation="vertical" />}
        </>
      )}
      {timeComp && cloneElement(timeComp, { ...timeProps, ...timeComp.props })}
    </Flex>
  );
};

type DatetimePickerDateProps = {
  /** Minimum selectable date (format: yyyy-MM-dd) */
  min?: string;
  /** Maximum selectable date (format: yyyy-MM-dd) */
  max?: string;
};

type DatetimePickerDateInjectedProps = {
  date: string | null;
  setDate: (value: string) => void;
  targetMonth: string | null;
  setTargetMonth: (month: string) => void;
};

type DatetimePickerTimeProps = {
  /** Minimum selectable time (format: HH:MM) */
  min?: string;
  /** Maximum selectable time (format: HH:MM) */
  max?: string;
  /** Time step interval (format: HH:MM) */
  step?: string;
  /** Whether to show the time toggle switch */
  hasTimeToggle?: boolean;
  onTimeToggleChange?: (enabled: boolean) => void;
};

type DatetimePickerTimeInjectedProps = {
  time: string | null;
  setTime: (value: string) => void;
  isTimeEnabled: boolean;
  setTimeEnabled: (enabled: boolean) => void;
};

export const DatetimePickerDate = ({
  min,
  max,
  date,
  setDate,
  targetMonth,
  setTargetMonth,
}: DatetimePickerDateProps & Partial<DatetimePickerDateInjectedProps>) => {
  if (setDate === undefined || setTargetMonth === undefined) {
    throw new Error("DatetimePicker.Date must be used within a DatetimePicker component");
  }

  const handleDateSelect = (selectedDate: string) => {
    if (min && selectedDate < min) return;
    if (max && selectedDate > max) return;

    setDate(selectedDate);
  };

  return (
    <Flex gap={8} direction="column">
      <DateSelector targetMonth={targetMonth || ""} onChangeValue={setTargetMonth} />
      <DatePicker
        targetMonth={targetMonth || ""}
        startValue={date}
        endValue={date}
        onSelect={handleDateSelect}
        min={min}
        max={max}
      />
    </Flex>
  );
};

export const DatetimePickerTime = forwardRef<
  TimePickerRefApi,
  DatetimePickerTimeProps & Partial<DatetimePickerTimeInjectedProps>
>(
  (
    {
      min,
      max,
      step,
      hasTimeToggle = false,
      onTimeToggleChange,
      time,
      setTime,
      isTimeEnabled,
      setTimeEnabled,
    },
    ref
  ) => {
    if (setTime === undefined || setTimeEnabled === undefined) {
      throw new Error("DatetimePicker.Time must be used within a DatetimePicker component");
    }

    return (
      <Flex direction="column" gap={8} grow={1} h="100%">
        {hasTimeToggle && (
          <Flex gap={8} alignItems="center">
            <InputSwitch
              checked={isTimeEnabled}
              onClick={() => {
                const nextEnabled = !isTimeEnabled;
                setTimeEnabled(nextEnabled);
                onTimeToggleChange?.(nextEnabled);
              }}
              size="small"
            />
            <Typography size="small" as="span" color={isTimeEnabled ? undefined : "#707579"}>
              Préciser l'heure
            </Typography>
          </Flex>
        )}
        <TimePicker
          ref={ref}
          value={time}
          onChangeValue={setTime}
          min={min}
          max={max}
          step={step}
          disabled={hasTimeToggle && !isTimeEnabled}
        />
      </Flex>
    );
  }
);

const isDatetimePickerDateComp = (
  node: any
): node is ReactElement<DatetimePickerDateProps, typeof DatetimePickerDate> =>
  isValidElement(node) && node.type === DatetimePickerDate;

const isDatetimePickerTimeComp = (
  node: any
): node is ReactElement<DatetimePickerTimeProps, typeof DatetimePickerTime> =>
  isValidElement(node) && node.type === DatetimePickerTime;
